// Sidebar.tsx — Left panel list
// Shows all panels in the project; active panel highlighted
// Buttons: Add, Duplicate, Delete (matches v11 batch-utilities concept)

import { useMemo, useState, useRef, useEffect } from 'react';
import { useShallowStore } from '../store/useStore';
import { getStructuralWarnings, countBySeverity } from '../utils/structuralWarnings';
import type { CVProject } from '../types/panelvar';

export default function Sidebar({ style }: { style?: React.CSSProperties }) {
  const {
    project,
    ui,
    addPanel,
    deletePanel,
    duplicatePanel,
    setActivePanel,
    setActiveDialog,
    showWarnings,
    toggleSidebar,
    toggleCheckedPanel,
    checkAllPanels,
    uncheckAllPanels,
  } = useShallowStore(s => ({
    project:            s.project,
    ui:                 s.ui,
    addPanel:           s.addPanel,
    deletePanel:        s.deletePanel,
    duplicatePanel:     s.duplicatePanel,
    setActivePanel:     s.setActivePanelId,
    setActiveDialog:    s.setActiveDialog,
    showWarnings:       s.showWarnings,
    toggleSidebar:      s.toggleSidebar,
    toggleCheckedPanel: s.toggleCheckedPanel,
    checkAllPanels:     s.checkAllPanels,
    uncheckAllPanels:   s.uncheckAllPanels,
  }));

  const panels = project?.panels ?? [];
  const activeId = ui.activePanelId;
  const checkedIds = ui.checkedPanelIds;

  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Close export menu on outside click
  useEffect(() => {
    if (!showExportMenu) return;
    function handleClick(e: MouseEvent) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showExportMenu]);

  // Compute structural warnings per panel (memoized on panels + settings)
  const panelWarnings = useMemo(() => {
    if (!project) return new Map<string, { errors: number; warns: number }>();
    const map = new Map<string, { errors: number; warns: number }>();
    for (const panel of project.panels) {
      const w = getStructuralWarnings(panel, (project as CVProject).settings);
      map.set(panel.id, countBySeverity(w));
    }
    return map;
  }, [project]);

  return (
    <aside className={`sidebar${ui.sidebarOpen ? '' : ' collapsed'}`} style={style}>
      <div className="sidebar-header">
        {ui.sidebarOpen ? (
          <>
            <span className="sidebar-title">PANELS</span>
            <div className="sidebar-header-actions">
              <button
                className="sidebar-add"
                title="Add new panel"
                onClick={() => addPanel()}
              >
                +
              </button>
              <button
                className="sidebar-collapse-btn"
                title="Collapse panel list"
                onClick={toggleSidebar}
              >
                ◀
              </button>
            </div>
          </>
        ) : (
          <button
            className="sidebar-expand-btn"
            title="Expand panel list"
            onClick={toggleSidebar}
          >
            ▶
          </button>
        )}
      </div>

      {ui.sidebarOpen && panels.length > 0 && (
        <div className="sidebar-select-bar">
          <button className="sidebar-select-btn" onClick={checkAllPanels}>All</button>
          <span className="sidebar-select-sep">|</span>
          <button className="sidebar-select-btn" onClick={uncheckAllPanels}>None</button>
          <span className="sidebar-checked-count">{checkedIds.length}/{panels.length}</span>
        </div>
      )}

      <ul className="sidebar-list">
        {panels.map((panel) => {
          const mark = panel.mpvar.panelMark || panel.id.slice(-6);
          const warnCounts = panelWarnings.get(panel.id);
          const hasErrors  = warnCounts && warnCounts.errors > 0;
          const badgeCount = warnCounts ? warnCounts.errors + warnCounts.warns : 0;
          const isChecked  = checkedIds.includes(panel.id);
          return (
            <li
              key={panel.id}
              className={`sidebar-item${panel.id === activeId ? ' active' : ''}`}
              onClick={() => setActivePanel(panel.id)}
            >
              <input
                type="checkbox"
                className="sidebar-checkbox"
                checked={isChecked}
                onChange={() => toggleCheckedPanel(panel.id)}
                onClick={(e) => e.stopPropagation()}
              />
              <span className="sidebar-mark">{mark}</span>
              {badgeCount > 0 && (
                <span
                  className={`sidebar-warn-badge${hasErrors ? ' error' : ''}`}
                  title={`${warnCounts!.errors} error(s), ${warnCounts!.warns} warning(s) — click to review`}
                  onClick={(e) => { e.stopPropagation(); showWarnings(panel.id); }}
                  style={{ cursor: 'pointer' }}
                >
                  {hasErrors ? '⛔' : '⚠️'} {badgeCount}
                </span>
              )}
              <span className="sidebar-dims">
                {panel.mpvar.width}" × {panel.mpvar.height}"
              </span>
              <div className="sidebar-actions">
                <button
                  title="Duplicate"
                  onClick={(e) => { e.stopPropagation(); duplicatePanel(panel.id); }}
                >
                  ⧉
                </button>
                <button
                  title="Delete"
                  className="danger"
                  onClick={(e) => { e.stopPropagation(); deletePanel(panel.id); }}
                >
                  ✕
                </button>
              </div>
            </li>
          );
        })}
        {panels.length === 0 && (
          <li className="sidebar-empty">No panels yet</li>
        )}
      </ul>

      {ui.sidebarOpen && (
        <div className="sidebar-footer" style={{ position: 'relative' }}>
          <button
            className="sidebar-footer-btn"
            title="Import panel from .cvpanel file"
            onClick={() => setActiveDialog('import-cv')}
          >
            ⬇ Import
          </button>
          <div ref={exportMenuRef} style={{ position: 'relative', flex: 1, display: 'flex' }}>
            <button
              className="sidebar-footer-btn"
              style={{ flex: 1 }}
              title="Export checked panels"
              disabled={checkedIds.length === 0}
              onClick={() => setShowExportMenu(!showExportMenu)}
            >
              ⬆ Export ▾
            </button>
            {showExportMenu && (
              <div className="sidebar-export-menu">
                <button className="sidebar-export-option" onClick={() => { setShowExportMenu(false); setActiveDialog('export-panel-dxf'); }}>
                  📐 DXF
                </button>
                <button className="sidebar-export-option" onClick={() => { setShowExportMenu(false); setActiveDialog('export-cvpanel'); }}>
                  📦 CV (.cvpanel)
                </button>
                <button className="sidebar-export-option" onClick={() => { setShowExportMenu(false); setActiveDialog('materials-list'); }}>
                  📋 Materials List
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}
